<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" @click.self="handleBackdropClick">
      <div class="flex items-center justify-center min-h-screen px-4 py-6">
        <!-- Backdrop -->
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-gray-900 opacity-75"></div>
        </div>

        <!-- Modal content -->
        <div
          class="relative bg-white dark:bg-gray-800 rounded-lg shadow-2xl transform transition-all w-full max-w-md mx-auto"
          @click.stop
        >
          <!-- Header -->
          <div class="px-6 pt-6 pb-4">
            <div class="flex items-center justify-center">
              <div
                class="flex items-center justify-center h-14 w-14 rounded-full"
                :class="statusIconClasses"
              >
                <component :is="statusIcon" class="h-8 w-8" :class="{ 'animate-spin': isActive }" />
              </div>
            </div>

            <h3 class="mt-4 text-xl font-semibold text-gray-900 dark:text-white text-center">
              {{ modalTitle }}
            </h3>

            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center">
              {{ modalSubtitle }}
            </p>
          </div>

          <!-- Progress section -->
          <div class="px-6 pb-6">
            <!-- Step indicator -->
            <div class="mb-4">
              <div
                class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2"
              >
                <span>{{ stepLabel }}</span>
                <span v-if="progress.total > 0"
                  >{{ progress.processed }} / {{ progress.total }}</span
                >
              </div>

              <!-- Progress bar -->
              <div class="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  class="h-full transition-all duration-300 ease-out rounded-full"
                  :class="progressBarClasses"
                  :style="{ width: progressPercentage + '%' }"
                ></div>
              </div>
            </div>

            <!-- Status message -->
            <div class="text-sm text-center text-gray-600 dark:text-gray-300">
              {{ progress.message || 'Bezig met verwerken...' }}
            </div>

            <!-- Steps visualization with labels -->
            <div class="mt-6">
              <div class="flex items-start justify-between max-w-xs mx-auto">
                <template v-for="(step, index) in steps" :key="step.key">
                  <!-- Step column -->
                  <div class="flex flex-col items-center w-12">
                    <div
                      class="flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-all duration-200"
                      :class="getStepClasses(step.key, index)"
                    >
                      <CheckIcon v-if="isStepCompleted(step.key)" class="w-4 h-4" />
                      <span v-else>{{ index + 1 }}</span>
                    </div>
                    <span class="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">{{
                      step.label
                    }}</span>
                  </div>
                  <!-- Connector line -->
                  <div
                    v-if="index < steps.length - 1"
                    class="flex-1 h-0.5 mt-4 mx-1"
                    :class="
                      isStepCompleted(step.key) ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    "
                  ></div>
                </template>
              </div>
            </div>

            <!-- Warning message -->
            <div
              v-if="isActive"
              class="mt-6 flex items-center justify-center text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded-lg px-4 py-3"
            >
              <ExclamationTriangleIcon class="h-5 w-5 mr-2 flex-shrink-0" />
              <span class="text-sm">Laat dit scherm open tot de synchronisatie klaar is.</span>
            </div>

            <!-- Error message -->
            <div
              v-if="hasError"
              class="mt-6 flex items-start text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-lg px-4 py-3"
            >
              <XCircleIcon class="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <div class="text-sm">
                <p class="font-medium">Er is een fout opgetreden</p>
                <p class="mt-1 text-red-500 dark:text-red-400">{{ errorMessage }}</p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-lg">
            <div class="flex justify-center space-x-3">
              <button
                v-if="hasError"
                @click="$emit('retry')"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowPathIcon class="h-4 w-4 mr-2" />
                Opnieuw proberen
              </button>

              <button
                v-if="!isActive"
                @click="$emit('close')"
                class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, watch, onMounted, onUnmounted } from 'vue'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CheckIcon
} from '@heroicons/vue/24/outline'
import { useScrollLock } from '@/composables/ui/useScrollLock'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  syncType: {
    type: String,
    default: 'employees',
    validator: value =>
      [
        'employees',
        'employees-all',
        'prospects',
        'hours',
        'all',
        'prospects-historical',
        'ml-training',
        'timeline-historical',
        'timeline-prospects'
      ].includes(value)
  },
  progress: {
    type: Object,
    default: () => ({
      active: false,
      step: null,
      processed: 0,
      total: 0,
      message: null
    })
  },
  hasError: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['close', 'retry'])

// Scroll lock
const { lockScroll, unlockScroll } = useScrollLock()

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      lockScroll()
    } else {
      unlockScroll()
    }
  },
  { immediate: true }
)

// Sync type titles
const syncTypeTitles = {
  employees: 'Medewerkers synchroniseren',
  'employees-all': 'Volledige medewerker synchronisatie',
  prospects: 'Projecten synchroniseren',
  hours: 'Uren synchroniseren',
  all: 'Volledige synchronisatie',
  'prospects-historical': 'Saleskansen importeren',
  'ml-training': 'Model trainen',
  'timeline-historical': 'Timeline-verrijking',
  'timeline-prospects': 'Timeline open deals verrijken',
  services: 'Diensten & facturen synchroniseren'
}

// Steps config per sync type
const syncTypeSteps = {
  'prospects-historical': {
    steps: [
      { key: 'starting', label: 'Start' },
      { key: 'fetching', label: 'Ophalen' },
      { key: 'saving-historical', label: 'Opslaan' },
      { key: 'done', label: 'Gereed' }
    ],
    order: ['starting', 'fetching', 'saving-historical', 'done']
  },
  'ml-training': {
    steps: [
      { key: 'starting', label: 'Start' },
      { key: 'done', label: 'Gereed' }
    ],
    order: ['starting', 'done']
  },
  'timeline-historical': {
    steps: [
      { key: 'starting', label: 'Start' },
      { key: 'timeline-enrichment', label: 'Historisch' },
      { key: 'timeline-prospects', label: 'Open deals' },
      { key: 'done', label: 'Gereed' }
    ],
    order: ['starting', 'timeline-enrichment', 'timeline-prospects', 'done']
  },
  'timeline-prospects': {
    steps: [
      { key: 'starting', label: 'Start' },
      { key: 'timeline-prospects', label: 'Verrijken' },
      { key: 'done', label: 'Gereed' }
    ],
    order: ['starting', 'timeline-prospects', 'done']
  }
}

const defaultStepConfig = {
  steps: [
    { key: 'starting', label: 'Start' },
    { key: 'fetching', label: 'Ophalen' },
    { key: 'saving', label: 'Opslaan' },
    { key: 'done', label: 'Gereed' }
  ],
  order: ['starting', 'fetching', 'saving', 'cleanup', 'done']
}

const steps = computed(() => (syncTypeSteps[props.syncType] || defaultStepConfig).steps)
const stepOrder = computed(() => (syncTypeSteps[props.syncType] || defaultStepConfig).order)

const isActive = computed(() => {
  return props.progress.active && !props.hasError
})

const modalTitle = computed(() => {
  if (props.hasError) return 'Synchronisatie mislukt'
  if (props.progress.step === 'done') return 'Synchronisatie voltooid'
  return syncTypeTitles[props.syncType] || 'Synchronisatie'
})

const modalSubtitle = computed(() => {
  if (props.hasError) return 'Er is een probleem opgetreden tijdens de synchronisatie.'
  if (props.progress.step === 'done') return 'De gegevens zijn succesvol bijgewerkt.'
  return 'Even geduld, de gegevens worden gesynchroniseerd...'
})

const stepLabel = computed(() => {
  const stepLabels = {
    starting: 'Starten...',
    fetching: 'Gegevens ophalen...',
    saving: 'Gegevens opslaan...',
    'saving-historical': 'Deals opslaan...',
    'timeline-enrichment': 'Historische deals verrijken...',
    'timeline-prospects': 'Open deals verrijken...',
    cleanup: 'Opruimen...',
    done: 'Voltooid'
  }
  return stepLabels[props.progress.step] || 'Bezig...'
})

const progressPercentage = computed(() => {
  if (props.progress.step === 'done') return 100
  if (props.hasError) return 0

  const currentStepIndex = stepOrder.value.indexOf(props.progress.step)
  if (currentStepIndex === -1) return 5

  // Base progress from step
  const stepProgress = (currentStepIndex / (stepOrder.value.length - 1)) * 100

  // Add sub-progress if we have total
  if (props.progress.total > 0) {
    const subProgress =
      (props.progress.processed / props.progress.total) * (100 / stepOrder.value.length)
    return Math.min(100, stepProgress + subProgress)
  }

  return Math.min(95, stepProgress)
})

const statusIcon = computed(() => {
  if (props.hasError) return XCircleIcon
  if (props.progress.step === 'done') return CheckCircleIcon
  return ArrowPathIcon
})

const statusIconClasses = computed(() => {
  if (props.hasError) return 'bg-red-100 text-red-600'
  if (props.progress.step === 'done') return 'bg-green-100 text-green-600'
  return 'bg-blue-100 text-blue-600'
})

const progressBarClasses = computed(() => {
  if (props.hasError) return 'bg-red-500'
  if (props.progress.step === 'done') return 'bg-green-500'
  return 'bg-blue-500'
})

const getStepClasses = stepKey => {
  const currentIndex = stepOrder.value.indexOf(props.progress.step)
  const stepIndex = stepOrder.value.indexOf(stepKey)

  if (props.hasError) return 'bg-gray-200 text-gray-500'

  if (stepIndex < currentIndex || props.progress.step === 'done') {
    return 'bg-green-500 text-white'
  }
  if (stepIndex === currentIndex) {
    return 'bg-blue-500 text-white'
  }
  return 'bg-gray-200 text-gray-500'
}

const isStepCompleted = stepKey => {
  if (props.hasError) return false
  const currentIndex = stepOrder.value.indexOf(props.progress.step)
  const stepIndex = stepOrder.value.indexOf(stepKey)
  return stepIndex < currentIndex || props.progress.step === 'done'
}

// Prevent closing while sync is active
const handleBackdropClick = () => {
  if (!isActive.value) {
    emit('close')
  }
}

// Escape closes the modal under the same conditions as the backdrop:
// never while a sync is actively running
const handleKeydown = e => {
  if (e.key === 'Escape' && props.isOpen && !isActive.value) {
    emit('close')
  }
}

// Prevent page unload during sync
const handleBeforeUnload = e => {
  if (isActive.value) {
    e.preventDefault()
    e.returnValue = 'De synchronisatie is nog bezig. Weet u zeker dat u wilt afsluiten?'
    return e.returnValue
  }
}

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('keydown', handleKeydown)
})
</script>
